import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Alert,
  Badge,
  Modal,
  Toast,
  ToastContainer,
  Pagination
} from 'react-bootstrap';
import {
  getComments, createComment, upvoteComment,
  // deleteComment
} from '../api';
import { getStyle, BWHeader, space6 } from '../dist/helpers';
import {
  // Audio utilities
  playNotificationSound,
  playUpvoteNotificationSound,
  initializeAudioContext,
  // Comment utilities
  sortComments,
  formatTimestamp,
  // getAllCommentIds,
  // getAllComments,
  getCommentDepth,
  extractUserId,
  validateCommentContent,
  // Pagination utilities
  getCurrentComments,
  calculateTotalPages,
  generateVisiblePageNumbers,
  getCommentRange,
  scrollToTop,
  // UI utilities
  getButtonStyles,
  getFormControlStyles,
  getSelectStyles,
  getPaginationItemStyles,
  getModalStyles,
  getNewCommentHighlightStyles,
  getTextColorClass,
  // State utilities
  createMessageHandler,
  handleNewComments,
  handleUpvoteChanges,
  updateCommentUpvotes,
  // removeComment
} from './Comments/index';
import './Comments.css';

const Comments = ({ dark, userData }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [, setNewRating] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyToComment, setReplyToComment] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
  const [newComments, setNewComments] = useState(new Set());
  const [newUpvotes, setNewUpvotes] = useState(new Set());
  const [totalUpvoteCount, setTotalUpvoteCount] = useState(0);
  const [sortBy, setSortBy] = useState('top');
  const [audioContext, setAudioContext] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [commentsPerPage] = useState(100);

  const showMessage = useCallback(() => {
    createMessageHandler(setToastMessage, setToastVariant, setShowToast)
  }, []);

  const handlePlayNotificationSound = useCallback(() => {
    playNotificationSound(audioContext);
  }, [audioContext]);

  const handlePlayUpvoteNotificationSound = useCallback(() => {
    playUpvoteNotificationSound(audioContext);
  }, [audioContext]);

  const fetchComments = useCallback(async (isAutoRefresh = false) => {
    if (!isAutoRefresh) {
      setIsLoading(true);
    }
    try {
      const result = await getComments();
      if (result.success) {
        if (isAutoRefresh) {
          setComments(prevComments => {
            if (prevComments.length > 0) {
              const updatedComments = handleNewComments(
                prevComments,
                result.comments,
                setNewComments,
                handlePlayNotificationSound,
                showMessage
              );

              handleUpvoteChanges(
                prevComments,
                result.comments,
                setNewUpvotes,
                setTotalUpvoteCount,
                handlePlayUpvoteNotificationSound,
                showMessage
              );

              return updatedComments;
            }
            return result.comments;
          });
        } else {
          setComments(result.comments);
        }

        setLastRefreshTime(new Date());
      } else {
        if (!isAutoRefresh) {
          showMessage(result.message || 'Failed to load comments', 'danger');
        }
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      if (!isAutoRefresh) {
        showMessage('Failed to load comments', 'danger');
      }
    } finally {
      if (!isAutoRefresh) {
        setIsLoading(false);
      }
    }
  }, [handlePlayNotificationSound, handlePlayUpvoteNotificationSound, showMessage]);

  useEffect(() => {
    const ctx = initializeAudioContext();
    setAudioContext(ctx);

    return () => {
      if (ctx) {
        ctx.close().catch(console.error);
      }
    };
  }, []);

  useEffect(() => {
    fetchComments();

    const refreshInterval = setInterval(() => {
      fetchComments(true);
    }, 1000);

    return () => clearInterval(refreshInterval);
  }, [fetchComments]);

  const currentComments = getCurrentComments(comments, currentPage, commentsPerPage, sortBy, sortComments);
  const totalPages = calculateTotalPages(comments.length, commentsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    scrollToTop();
  };

  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    const validation = validateCommentContent(newComment);
    if (!validation.isValid) {
      showMessage(validation.message, 'warning');
      return;
    }

    const userId = extractUserId(userData);
    if (!userId) {
      showMessage('User ID not found. Please log in again.', 'warning');
      return;
    }

    setIsLoading(true);
    try {
      const result = await createComment(newComment.trim(), userId);
      if (result.success) {
        setNewComment('');
        setNewRating(0);
        showMessage('Comment submitted successfully!');
        resetToFirstPage();
        await fetchComments();
      } else {
        showMessage(result.message || 'Failed to submit comment', 'danger');
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      showMessage('Failed to submit comment', 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLike = async (commentId) => {
    try {
      const result = await upvoteComment(commentId);
      if (result.success) {
        setComments(prev => updateCommentUpvotes(prev, commentId, result.upvotes));

        setNewUpvotes(prev => {
          const newSet = new Set([...prev, commentId]);
          return newSet;
        });

        setTotalUpvoteCount(prev => prev + 1);
        handlePlayUpvoteNotificationSound();

        setTimeout(() => {
          setNewUpvotes(prev => {
            const updated = new Set(prev);
            updated.delete(commentId);
            return updated;
          });
          setTotalUpvoteCount(0);
        }, 10000);

        showMessage('Comment upvoted!', 'success');
      } else {
        showMessage(result.message || 'Failed to upvote comment', 'danger');
      }
    } catch (error) {
      console.error('Error upvoting comment:', error);
      showMessage('Failed to upvote comment', 'danger');
    }
  };

  const handleReply = (comment) => {
    const depth = getCommentDepth(comment.id, comments);
    if (depth >= 5) {
      showMessage('Maximum nesting depth reached. Cannot reply to this comment.', 'warning');
      return;
    }

    setReplyToComment(comment);
    setReplyText('');
    setShowReplyModal(true);
  };

  const submitReply = async () => {
    const validation = validateCommentContent(replyText);
    if (!validation.isValid) {
      showMessage(validation.message, 'warning');
      return;
    }

    const userId = extractUserId(userData);
    if (!userId) {
      showMessage('User ID not found. Please log in again.', 'warning');
      return;
    }

    setIsLoading(true);
    try {
      const result = await createComment(replyText.trim(), userId, replyToComment.id);
      if (result.success) {
        setShowReplyModal(false);
        setReplyToComment(null);
        setReplyText('');
        showMessage('Reply submitted successfully!');
        await fetchComments();
      } else {
        showMessage(result.message || 'Failed to submit reply', 'danger');
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      showMessage('Failed to submit reply', 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // TODO - make it such that only Admins can delete comments
  // const handleDelete = async (commentId) => {
  //   setIsLoading(true);
  //   try {
  //     const result = await deleteComment(commentId);
  //     if (result.success) {
  //       // Use utility function to remove comment
  //       setComments(prev => removeComment(prev, commentId));
  //       showMessage('Comment deleted successfully!');
  //     } else {
  //       showMessage(result.message || 'Failed to delete comment', 'danger');
  //     }
  //   } catch (error) {
  //     console.error('Error deleting comment:', error);
  //     showMessage('Failed to delete comment', 'danger');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const renderComment = (comment, isReply = false, depth = 0) => (
    <div
      key={comment.id}
      className={`h-100 p-4 mb-4 ${isReply ? `ms-${Math.min(depth * 2, 8)} nested-comment` : ''} ${newComments.has(comment.id) ? 'new-comment-highlight' : ''}`}
      style={{
        ...(getStyle(dark)),
        ...(newComments.has(comment.id) && getNewCommentHighlightStyles(dark))
      }}
    >
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div className="d-flex align-items-center">
          <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
            <i className="fas fa-user"></i>
          </div>
          <div>
            <h5 className={`mb-1 ${getTextColorClass(dark)}`}>
              {comment.user?.username || `User ${comment.user_id}` || 'Unknown User'}
              {isReply && (
                <span className="reply-indicator">
                  <i className="fas fa-reply me-1"></i>
                  Reply
                </span>
              )}
              {newComments.has(comment.id) && (
                <span className="new-comment-badge">
                  <i className="fas fa-star me-1"></i>
                  NEW
                </span>
              )}
            </h5>
          </div>
        </div>
        <small className={getTextColorClass(dark, 'secondary')}>
          {formatTimestamp(comment.created_at)}
        </small>
      </div>

      <p className={`mb-3 ${getTextColorClass(dark)}`}>
        {comment.content}
      </p>

      {!isReply && comment.rating && comment.rating > 0 && (
        <div className="mb-3">
          <div className="text-warning">
            {[...Array(comment.rating)].map((_, i) => (
              <i key={i} className="fas fa-star"></i>
            ))}
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <button
            className={`btn btn-light me-2 comment-action-btn ${newUpvotes.has(comment.id) ? 'new-upvote-highlight' : ''}`}
            onClick={() => handleLike(comment.id)}
            style={getButtonStyles(dark, newUpvotes.has(comment.id) ? 'highlight' : 'default')}
          >
            👍 {comment.upvotes || 0}
            {newUpvotes.has(comment.id) && (
              <span className="ms-1 upvote-indicator">
                <i className="fas fa-arrow-up"></i>
              </span>
            )}
          </button>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-light comment-action-btn"
            onClick={() => handleReply(comment)}
            title="Reply"
            disabled={depth >= 5}
            style={getButtonStyles(dark)}
          >
            <i className="fas fa-reply"></i>
            {depth >= 5 && <span className="ms-1 max-depth-warning">(Max depth)</span>}
          </button>

          {/* <button
            className="btn btn-light comment-action-btn"
            onClick={() => handleDelete(comment.id)}
            title="Delete"
            style={getButtonStyles(dark, 'danger')}
          >
            <i className="fas fa-trash"></i>
          </button> */}
        </div>
      </div>

      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-4">
          <hr className="mb-3" />
          <small className={getTextColorClass(dark, 'secondary')}>
            Replies ({comment.replies.length}):
          </small>
          {comment.replies.map(reply => renderComment(reply, true, depth + 1))}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <center>
        {BWHeader(dark, "Comments & Feedback", "Share your thoughts, suggestions, or ask questions")}
        <br />

        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>

          {!userData ? (
            <div className="h-100 p-4 mb-4" style={getStyle(dark)}>
              <Alert variant="info" className="text-center mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Please <a href="/login" className="alert-link">log in</a> to post comments and replies.
              </Alert>
            </div>
          ) : (
            <div className="h-100 p-4 mb-4" style={{ ...(getStyle(dark)), margin: "00px 20px 00px 20px" }}>
              <Form onSubmit={handleSubmitComment}>
                <Form.Group className="mb-3">
                  <Form.Label className={getTextColorClass(dark)}>
                    Post a Comment
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (newComment.trim() && !isLoading) {
                          handleSubmitComment(e);
                        }
                      }
                    }}
                    placeholder="Share your thoughts... (Press Enter to submit, Shift+Enter for new line)"
                    style={getFormControlStyles(dark)}
                  />
                </Form.Group>

                <button
                  type="submit"
                  className="btn btn-light w-100"
                  disabled={!newComment.trim() || isLoading}
                  style={{
                    ...getButtonStyles(dark),
                    padding: "0.75rem 1.5rem",
                    marginTop: "1rem"
                  }}
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-2"></i>
                      Submit Comment
                    </>
                  )}
                </button>
              </Form>
            </div>
          )}

          <h3 className={getTextColorClass(dark)}>
            Comments
          </h3>

          <div style={{ margin: "0 20px 20px 20px" }}>
            <div className="auto-refresh-indicator" style={{ textAlign: 'center' }}>
              <span style={{ color: dark ? 'white' : 'inherit' }}>
                  Last Refresh: {lastRefreshTime.toLocaleTimeString()}
              </span>
            </div>
          </div>

          <div style={{ margin: "0 20px 0 20px" }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center">
                <h3 className={getTextColorClass(dark)}>
                  {newComments.size > 0 && (
                    <span className="ms-2 new-comments-counter">
                      <i className="fas fa-star me-1"></i>
                      {newComments.size} new
                    </span>
                  )}
                  {totalUpvoteCount > 0 && (
                    <span className="ms-2 new-upvotes-counter">
                      <i className="fas fa-arrow-up me-1"></i>
                      {totalUpvoteCount} upvotes
                    </span>
                  )}
                </h3>
              </div>
              <div className="d-flex align-items-center">
                <div className="me-3 d-flex align-items-center">
                  <select
                    className="form-select me-2"
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      resetToFirstPage();
                    }}
                    style={getSelectStyles(dark)}
                  >
                    <option value="top">🔥 Top Voted </option>
                    <option value="newest">🕒 Newest</option>
                    <option value="oldest">📅 Oldest</option>
                  </select>
                </div>
                <Badge bg="secondary">
                  {totalPages > 1 ? `${currentPage}/${totalPages}` : comments.length}
                </Badge>
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-5">
                <div className="d-flex justify-content-center align-items-center">
                  <i className="fas fa-spinner fa-spin me-2" style={{ fontSize: '1.5rem' }}></i>
                  <span className={getTextColorClass(dark, 'secondary')}>
                    Loading comments...
                  </span>
                </div>
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-5">
                <p className={getTextColorClass(dark, 'secondary')}>
                  No comments yet. Be the first to share your thoughts!
                </p>
              </div>
            ) : (
              <div>
                {currentComments.map(comment => renderComment(comment))}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-4 mb-4">
                    <Pagination>
                      <Pagination.First
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        style={getPaginationItemStyles(dark, false)}
                      />

                      <Pagination.Prev
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={getPaginationItemStyles(dark, false)}
                      />

                      {generateVisiblePageNumbers(currentPage, totalPages).map(pageNumber => (
                        <Pagination.Item
                          key={pageNumber}
                          active={pageNumber === currentPage}
                          onClick={() => handlePageChange(pageNumber)}
                          style={getPaginationItemStyles(dark, pageNumber === currentPage)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      ))}

                      <Pagination.Next
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={getPaginationItemStyles(dark, false)}
                      />

                      <Pagination.Last
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        style={getPaginationItemStyles(dark, false)}
                      />
                    </Pagination>
                  </div>
                )}

                {totalPages > 1 && (
                  <div className="text-center mb-4">
                    <small className={getTextColorClass(dark, 'secondary')}>
                      {(() => {
                        const { start, end } = getCommentRange(currentPage, commentsPerPage, comments.length);
                        return `Showing ${start} to ${end} of ${comments.length} comments (Page ${currentPage} of ${totalPages})`;
                      })()}
                    </small>
                  </div>
                )}

                <br />
                <br />
                <br />
                <br />
              </div>
            )}
          </div>
        </div>

        {space6}
        <br />
      </center>

      <Modal
        show={showReplyModal}
        onHide={() => setShowReplyModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={getModalStyles(dark)}
        >
          <Modal.Title className={getTextColorClass(dark)}>
            Reply to Comment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={getModalStyles(dark)}
        >
          <div className={`mb-3 p-3 rounded ${dark ? 'bg-dark' : 'bg-light'}`}>
            <p className={`mb-2 ${getTextColorClass(dark, 'secondary')}`}>
              <i className="fas fa-reply me-1"></i>
              Replying to:
            </p>
            <p className={`mb-1 ${getTextColorClass(dark)}`}>
              <strong>{replyToComment?.user?.username || `User ${replyToComment?.user_id}` || 'Unknown User'}</strong>
              {replyToComment?.parent_id && (
                <span className="reply-indicator ms-2">
                  <i className="fas fa-level-up-alt me-1"></i>
                  Nested reply
                </span>
              )}
            </p>
            <p className={getTextColorClass(dark, 'muted')}>
              {replyToComment?.content}
            </p>
          </div>
          <Form.Control
            as="textarea"
            rows={4}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (replyText.trim() && !isLoading) {
                  submitReply();
                }
              }
            }}
            placeholder="Write your reply... (Press Enter to submit, Shift+Enter for new line)"
            style={{
              backgroundColor: dark ? '#1a202c' : '#ffffff',
              color: dark ? '#e2e8f0' : '#2d3748',
              border: `1px solid ${dark ? '#4a5568' : '#e2e8f0'}`
            }}
          />
        </Modal.Body>
        <Modal.Footer
          style={getModalStyles(dark)}
        >
          <button
            className="btn btn-light me-2"
            onClick={() => setShowReplyModal(false)}
            style={getButtonStyles(dark)}
          >
            Cancel
          </button>
          <button
            className="btn btn-light"
            onClick={submitReply}
            disabled={!replyText.trim()}
            style={getButtonStyles(dark)}
          >
            Submit Reply
          </button>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        position="bottom-end"
        className="p-3"
        style={{ zIndex: 9999 }}
      >
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={4000}
          autohide
          bg={toastVariant}
        >
          <Toast.Header closeButton>
            <strong className="me-auto">
              {toastVariant === 'success' ? '✅ Success' :
                toastVariant === 'danger' ? '❌ Error' :
                  toastVariant === 'warning' ? '⚠️ Warning' : 'ℹ️ Info'}
            </strong>
          </Toast.Header>
          <Toast.Body className={toastVariant === 'danger' ? 'text-white' : ''}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Comments;
